import React, {
  useState,
  useCallback,
  useMemo,
  useRef,
  ChangeEvent,
  MouseEvent,
  FormEvent,
} from 'react';
import { useQuery } from '@apollo/client/react';
import { Avatar, SearchUserResult } from '@elevator/ui';
import InputAdornment from '@mui/material/InputAdornment';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import SearchIcon from '@mui/icons-material/Search';
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';
import { SEARCH_USERS } from 'lib/graphql/queries/user';
import useStyles from './styles';
import AddUserMenuItem from './AddUserMenuItem';
import Autosuggest, {
  SuggestionsFetchRequestedParams,
  RenderInputComponentProps,
  RenderSuggestionsContainerParams,
  ChangeEvent as AutosuggestChangeEvent,
} from 'react-autosuggest';

/**
 * UserSearchInput component provides an autocomplete input for searching users.
 * Uses react-autosuggest library to display user suggestions with avatars and highlighted text.
 *
 * @param props - Component props
 * @returns UserSearchInput component
 */
const UserSearchInput = (props: UserSearchInputProps) => {
  const {
    id,
    label,
    placeholder,
    disableUnderline,
    onUserSelected,
    textFieldInput,
    setTextFieldInput,
    disabled,
    isAddCreditOption,
    handleManualAddNew,
    defaultOpen,
    forceSuggestionsOpen,
  } = props;

  const { classes } = useStyles();

  const [inputHasFocus, setInputHasFocus] = useState(false);
  // When parent provides an initial value (e.g. story with dropdown open), seed the
  // search so suggestions load without waiting for user input.
  const [searchInput, setSearchInput] = useState(() =>
    textFieldInput.length >= 3 ? textFieldInput : '',
  );
  const hasFocusedRef = useRef(false);

  // Search Query - use searchInput as variable so results update when user types
  const { data } = useQuery<{ searchUsers: SearchUserResult[] }>(SEARCH_USERS, {
    variables: { input: searchInput },
    skip: searchInput.length < 3,
    errorPolicy: 'all',
  });

  const usernameSuggestions = useMemo(() => {
    return data?.searchUsers || [];
  }, [data]);

  /**
   * Renders a single list item based on the list of generated suggestions.
   * Displays user avatar and username with highlighted matching text.
   */
  const renderSuggestion = useCallback(
    (
      suggestion: SearchUserResult,
      { query, isHighlighted }: { query: string; isHighlighted: boolean },
    ) => {
      const matches = match(suggestion.preferredUsername, query);
      const parts = parse(suggestion.preferredUsername, matches);

      return (
        <MenuItem
          selected={isHighlighted}
          onMouseDown={(e: MouseEvent) => e.preventDefault()}
          component="div"
          aria-label={`Select user ${suggestion.preferredUsername}`}
        >
          <ListItemAvatar>
            <Avatar
              src={suggestion.avatar?.sourceUrl}
              srcSet={suggestion.avatar?.sourceSet}
              username={suggestion.preferredUsername}
              size="small"
            />
          </ListItemAvatar>
          <ListItemText
            primary={parts.map((part, index) => {
              return part.highlight ? (
                <Typography key={String(index)} component="span" variant="subtitle2">
                  {part.text}
                </Typography>
              ) : (
                <Typography variant="body2" key={String(index)} component="span">
                  {part.text}
                </Typography>
              );
            })}
          />
        </MenuItem>
      );
    },
    [],
  );

  /**
   * Utility function, retrieves the preferredUsername from a suggestion object.
   */
  const getSuggestionValue = useCallback((suggestion: SearchUserResult) => {
    return suggestion.preferredUsername;
  }, []);

  const handleTextFieldInputChange = useCallback(
    (_event: FormEvent<HTMLElement>, { newValue }: AutosuggestChangeEvent) => {
      setTextFieldInput(newValue);
    },
    [setTextFieldInput],
  );

  const handleSuggestionSelected = useCallback(
    (
      _event: React.FormEvent<HTMLElement>,
      { suggestionValue }: { suggestionValue: string },
    ) => {
      const selectedUser = usernameSuggestions.find(
        (user) => user.preferredUsername === suggestionValue,
      );
      if (selectedUser) {
        onUserSelected(selectedUser);
      }
    },
    [usernameSuggestions, onUserSelected],
  );

  /**
   * Renders the input component (TextField with search icon).
   */
  const renderInput = useCallback(
    (inputProps: RenderInputComponentProps) => {
      const {
        value,
        onChange,
        ref,
        color: _color,
        size: _size,
        className: _autosuggestClassName,
        ...other
      } = inputProps;

      // Label: use label prop or fall back to placeholder (e.g. "Username").
      // Keep label always shrunk so height matches the adjacent Role select (FormControl + InputLabel shrink).
      const displayLabel = label || placeholder;

      const inputRefCallback = (el: HTMLInputElement | null) => {
        if (ref) {
          if (typeof ref === 'function') ref(el);
          else (ref as React.MutableRefObject<HTMLInputElement | null>).current = el;
        }
        if (defaultOpen && el && !hasFocusedRef.current) {
          hasFocusedRef.current = true;
          setTimeout(() => el.focus(), 0);
        }
      };

      return (
        <TextField
          variant="filled"
          value={value || ''}
          label={displayLabel}
          onChange={onChange}
          inputRef={inputRefCallback}
          fullWidth
          {...other}
          slotProps={{
            inputLabel: { shrink: true, sx: { marginTop: '6px' } },
            input: {
              disableUnderline: disableUnderline ?? true,
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color={disabled ? 'disabled' : 'action'} fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
          disabled={disabled}
          inputProps={{
            'aria-label': displayLabel || 'Search users by username',
          }}
        />
      );
    },
    [label, placeholder, disableUnderline, disabled, defaultOpen],
  );

  const showSuggestions = inputHasFocus || forceSuggestionsOpen;

  const shouldRenderSuggestions = useCallback(
    (value: string) => forceSuggestionsOpen || value.trim().length >= 3,
    [forceSuggestionsOpen],
  );

  const renderSuggestionContainer = useCallback(
    (params: RenderSuggestionsContainerParams) => {
      const { containerProps, children, query } = params;
      const { key, ...restContainerProps } = containerProps;

      // There are results to show
      if (children !== null && showSuggestions) {
        return (
          <Paper
            elevation={3}
            key={key}
            {...(restContainerProps as Record<string, unknown>)}
            role="listbox"
            aria-label="User suggestions"
          >
            {children}
            <AddUserMenuItem
              isAddCreditOption={isAddCreditOption}
              isHighlighted={false}
              handleManualAddNew={handleManualAddNew}
            />
          </Paper>
        );
      } else if (children === null && showSuggestions && query.length !== 0) {
        // There are no results to show but we want to tell the user
        return (
          <Paper
            elevation={3}
            key={key}
            {...(restContainerProps as Record<string, unknown>)}
            className={classes.noResultsFoundContainer}
            role="listbox"
            aria-label="No user suggestions"
          >
            <Typography variant="overline2" className={classes.noResultsFoundText}>
              No users found
            </Typography>
            <AddUserMenuItem
              isAddCreditOption={isAddCreditOption}
              isHighlighted={false}
              handleManualAddNew={handleManualAddNew}
            />
          </Paper>
        );
      }

      // Render nothing
      return null;
    },
    [
      showSuggestions,
      isAddCreditOption,
      handleManualAddNew,
      classes.noResultsFoundContainer,
      classes.noResultsFoundText,
    ],
  );

  const handleSuggestionsClearRequested = useCallback(() => {
    if (forceSuggestionsOpen) return;
    setSearchInput('');
  }, [forceSuggestionsOpen]);

  const handleSuggestionsFetchRequested = useCallback(
    ({ value }: SuggestionsFetchRequestedParams) => {
      setSearchInput(value);
    },
    [],
  );

  return (
    <div data-component="UserSearchInput" style={{ width: '100%' }}>
      <Autosuggest
        id={id}
        shouldRenderSuggestions={shouldRenderSuggestions}
        alwaysRenderSuggestions={forceSuggestionsOpen}
        theme={{
          container: classes.root,
          suggestionsContainer: classes.suggestionsContainer,
          suggestionsContainerOpen: classes.suggestionsContainerOpen,
          suggestionsList: classes.suggestionsList,
          suggestion: classes.suggestion,
        }}
        renderInputComponent={renderInput}
        suggestions={usernameSuggestions}
        getSuggestionValue={getSuggestionValue}
        renderSuggestion={renderSuggestion}
        onSuggestionSelected={handleSuggestionSelected}
        focusInputOnSuggestionClick={false}
        highlightFirstSuggestion
        inputProps={{
          value: textFieldInput,
          onChange: handleTextFieldInputChange,
          onBlur: () => setInputHasFocus(false),
          onFocus: () => setInputHasFocus(true),
        }}
        onSuggestionsFetchRequested={handleSuggestionsFetchRequested}
        renderSuggestionsContainer={renderSuggestionContainer}
        onSuggestionsClearRequested={handleSuggestionsClearRequested}
      />
    </div>
  );
};

export interface UserSearchInputProps {
  /** Unique identifier for the input */
  id: string;
  /** Label for the text field */
  label?: string;
  /** Placeholder text for the input (if not provided, label will act as placeholder) */
  placeholder?: string;
  /** Whether to disable the underline on the input */
  disableUnderline?: boolean;
  /** Callback function called when a user is selected from suggestions */
  onUserSelected: (user: SearchUserResult) => void;
  /** Current value of the text field input */
  textFieldInput: string;
  /** Function to update the text field input value */
  setTextFieldInput: (input: string) => void;
  /** Whether the input is disabled */
  disabled: boolean;
  /** Whether to show the "Add New" menu option */
  isAddCreditOption?: boolean;
  /** Callback function called when "Add New" is clicked */
  handleManualAddNew: () => void;
  /** When true, focus the input on mount so the dropdown opens (e.g. for Storybook) */
  defaultOpen?: boolean;
  /** When true, keep the suggestions panel mounted even when the input blurs (Storybook/docs only) */
  forceSuggestionsOpen?: boolean;
}

export default UserSearchInput;
